import { Component, HostListener, Input, NgZone, OnInit } from "@angular/core";
import { ModuleManager } from "src/shared/models/module-manager";
import { GaugeService } from "./gauge.service";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import * as Highcharts from "highcharts";
import HC_more from "highcharts/highcharts-more"; // load highcharts-more
import { CxoneGaugeModuleData, GaugeModuleDisplay, GaugeObject, PlotbandsObj } from "./cxone-gauge.models";
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from "src/shared/models/module-manager/module-manager-menu-item.model";
import { ModuleManagerRefreshType } from "../../../../../shared/models/module-manager/module-manager-options.model";

@Component({
    selector: "cxone-gauge",
    templateUrl: "./gauge.component.html",
    styleUrls: ["./gauge.component.scss"],
})
export class GaugeComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    public view = 0;
    public highcharts = Highcharts;
    public chartOptions: Highcharts.Options;
    public gaugeData: CxoneGaugeModuleData;
    public noData = false;
    public plotbands: PlotbandsObj[] = [];
    public plotBandsDefault: PlotbandsObj[] = [];
    public intervalRangeTooWide = false;
    public isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    abortLoad: () => void = null;
    public gaugeDisplayValue: string;
    public chartData = null;
    public gaugeColors: string[] = [];
    public numberTypes = ['Currency', 'Number', 'Percent', 'Time'];
    public showColorIndicator = false;
    public moduleDisplay: GaugeModuleDisplay = GaugeModuleDisplay.Compact;
    private gaugeContext = {
        viewStateLoaded: () => this.moduleDisplay === GaugeModuleDisplay.Detailed,
        settingsSelectedInterval: null,
    };

    constructor(
        public cvLocalizationService: CVLocalizationService,
        public service: GaugeService,
        private zone: NgZone
    ) { }

    @HostListener('window:resize', ['$event'])
    onResize(e: any) {
        window.requestAnimationFrame(() => {
            if (this.gaugeData) {
                this.renderGaugeChart(this.gaugeData.GaugeData);
            }
        });
    }

    async ngOnInit() {
        HC_more(this.highcharts); // init highcharts-more
        await this.cvLocalizationService.GetSection("Gauge_Module");
        this.moduleManager.container.dashboardManager.moduleResizeStopped.subscribe((e: any) => {
            if (this.moduleKey === e.item.moduleKey) {
                window.requestAnimationFrame(() => {
                    if (this.gaugeData) {
                        this.renderGaugeChart(this.gaugeData.GaugeData);
                    }
                });
            }
        });
        this.moduleManager.setOptions({
            name: "Gauge",
            widgetMode: false,
            onSettingsApplied: (settings) =>
                this.zone.run(() => {
                    this.loadData(false);
                    this.gaugeContext.settingsSelectedInterval = settings.intervalType;
                }),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: (isAutoRefresh) =>
                this.zone.run(() => this.loadData(isAutoRefresh)),
            canRefresh: () => true,
            onExport: (format) => this.zone.run(() => this.service.LoadDataForExport(this.moduleKey, format)),
        });

        this.moduleManager.addState({
            name: "viewModelGauge",
            autoLoad: true,
            getStateData: () => this.moduleDisplay,
            onStateLoaded: (stateData) => {
                if (stateData == null) {
                    return;
                }
                this.view = stateData;
                this.moduleDisplay = stateData ? stateData : GaugeModuleDisplay.Compact;
            },
        });
        this.resetMenuItems();
    }

    async loadData(isAutoRefresh: boolean): Promise<CxoneGaugeModuleData> {
        if (this.isDataLoading) {
            return; // don't have multiple outgoing requests
        }
        this.isDataLoading = true;
        const abort = new Promise<void>((resolve, reject) => this.abortLoad = () => resolve());
        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }
        this.moduleManager.preventModuleMapUpdate();
        try {
            if (this.moduleManager.getModuleSettings().selectedMetrics.length > 0) {
                this.gaugeData = await this.service.loadGaugeData(this.moduleKey, abort);
            }
            if (this.gaugeData == null) {
                this.moduleManager.showLoader = false;
                return;
            } else if (this.gaugeData.Error === 1) {
                this.noData = true;
                this.intervalRangeTooWide = false;
            } else if (this.gaugeData.Error === 2) {
                this.noData = false;
                this.intervalRangeTooWide = true;
            } else {
                this.noData = false;
                this.intervalRangeTooWide = false;
                if (this.gaugeData) {
                    this.renderGaugeChart(this.gaugeData.GaugeData);
                }
            }
        } catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
            this.moduleManager.setAutoRefreshReady();
            this.moduleManager.removeModuleMapPrevention();
        }
        this.moduleManager.showLoader = false;
    }

    renderGaugeChart(gaugeData: GaugeObject) {
        if (gaugeData === undefined || gaugeData.Objective === null) {
            this.getDisplayValue(this.gaugeData.GaugeData);
            return;
        }
        this.gaugeColors = gaugeData.Objective.ColorRange.HexColor;
        const div = 200 / gaugeData.Objective.Max;
        const tickPosition: number[] = [];
        const plotbands: PlotbandsObj[] = [];
        const plotBandsDefault: PlotbandsObj[] = [];
        const borderColor = '#798a92';
        const defaultBandColor = '#D2D8DB';
        const gaugeValue: number = gaugeData.Value !== null ? gaugeData.Value : 0;
        const thickness: any = '20%';
        const numberType = this.numberTypes[gaugeData.NumericType];
        gaugeData.Objective.Values.forEach((item, i) => {
            tickPosition.push(item * div);
            const plotBandData: PlotbandsObj = {
                from: i === 0 ? 0 : gaugeData.Objective.Values[i - 1] * div,
                to: item * div,
                color: gaugeData.Objective.ColorRange.HexColor[i],
                thickness,
                borderColor,
                borderWidth: 1,
                events: {
                    mousemove: (e) => {
                        const toolTipText = document.getElementById(`label-${this.moduleKey}-${item * div}`).innerText;
                        const x = e.layerX + 10;
                        const y = e.layerY + 10;
                        if (!this.chartData.hasOwnProperty('plotBandsTooltip')) {
                            this.chartData.plotBandsTooltip = [];
                        }
                        if (!this.chartData.plotBandsTooltip[i]) {
                            this.chartData.plotBandsTooltip[i] = this.chartData.renderer.label(
                                toolTipText, x, y, 'callout'
                            ).attr({
                                fill: "#fff",
                                zIndex: 10,
                                padding: 5,
                                r: 5,
                                width: '100%',
                                color: "#353a40",
                            }).css({
                                transition: 'opacity 0.5s',
                                opacity: 1
                            }).add();
                        }
                        else {
                            this.chartData.plotBandsTooltip[i].attr({
                                x,
                                y,
                            }).css({
                                visibility: 'visible'
                            });
                        }
                        this.chartData.plotBandsTooltip[i].text.element.textContent = toolTipText;
                    },
                    mouseout: () => {
                        this.chartData.plotBandsTooltip[i].css({
                            visibility: 'hidden'
                        });
                    }
                }
            };
            let breakpointSliceColor = '';
            if (gaugeData.NumericType === 0 || gaugeData.NumericType === 1 || gaugeData.NumericType === 2) {
                const from = i === 0 ? 0 : gaugeData.Objective.Values[i - 1];
                const to = item;
                breakpointSliceColor = from <= gaugeData.Value && gaugeData.Value < to ? plotBandData.color : '';
            } else if (gaugeData.NumericType === 3) {
                let convertedValToSec = Number(gaugeValue.toFixed(2));
                if (gaugeData.TimeType === 2) {
                    convertedValToSec = Number(gaugeValue.toFixed(2)) * 60;
                } else if (gaugeData.TimeType === 3) {
                    convertedValToSec = (Number(gaugeValue.toFixed(2)) * 60) * 60;
                }
                const from = i === 0 ? 0 : gaugeData.Objective.Values[i - 1];
                const to = item;
                breakpointSliceColor = from <= convertedValToSec && convertedValToSec <= to ? plotBandData.color : '';
            }
            plotbands.push(plotBandData);
            plotBandsDefault.push({ ...plotBandData, ...{ color: breakpointSliceColor === '' ? defaultBandColor : breakpointSliceColor } });
        });
        let sliceColorForMax = defaultBandColor;
        if (gaugeData.Objective.Values[gaugeData.Objective.Values.length - 1] <= gaugeData.Value
            && gaugeData.Value <= gaugeData.Objective.Max) {
            sliceColorForMax = gaugeData.Objective.ColorRange.HexColor[gaugeData.Objective.ColorRange.HexColor.length - 1];
        }
        const plotBand: PlotbandsObj = {
            from: gaugeData.Objective.Values[gaugeData.Objective.Values.length - 1] * div,
            to: gaugeData.Objective.Max * div,
            color: gaugeData.Objective.ColorRange.HexColor[gaugeData.Objective.ColorRange.HexColor.length - 1], // green
            thickness,
            borderColor,
            borderWidth: 1,
        };
        plotbands.push(plotBand);
        plotBandsDefault.push({ ...plotBand, ...{ color: sliceColorForMax } });
        let breakPoint: number[] = [0];
        const centerTitle: string = this.getDisplayValue(gaugeData);
        breakPoint = gaugeValue < gaugeData.Objective.Max ? [gaugeValue * div] : [gaugeData.Objective.Max * div];
        let gaugeValueForWhiteLine: number = gaugeData.Value;
        if (gaugeData.NumericType === 3) {
            let convertedValToSec = Number(gaugeValue.toFixed(2));
            if (gaugeData.TimeType === 2) {
                convertedValToSec = Number(gaugeValue.toFixed(2)) * 60;
            } else if (gaugeData.TimeType === 3) {
                convertedValToSec = (Number(gaugeValue.toFixed(2)) * 60) * 60;
            }
            gaugeValueForWhiteLine = convertedValToSec;
            breakPoint = convertedValToSec < gaugeData.Objective.Max ? [convertedValToSec * div] : [gaugeData.Objective.Max * div];

        }
        if (gaugeValueForWhiteLine > 0 && gaugeValueForWhiteLine < gaugeData.Objective.Max) {
            const plotBandVal: PlotbandsObj = {
                from: gaugeValueForWhiteLine * div,
                to: (gaugeValueForWhiteLine * div) + 1,
                color: 'white',
                thickness,
                borderColor: "white",
                borderWidth: 1,
            };
            plotbands.push(plotBandVal);
            plotBandsDefault.push(plotBandVal);
        }
        this.plotbands = [...[], ...plotbands];
        this.plotBandsDefault = [...[], ...plotBandsDefault];
        const width = document.getElementById("gauge-body-" + this.moduleKey).clientWidth;
        const height = document.getElementById("gauge-body-" + this.moduleKey).clientHeight;
        this.customPlugin(this.highcharts);
        const largeHeight = height / 7;
        const mediumHeight = height / 9;
        const smallHeight = height / 25;
        const chartOptions: Highcharts.Options = {
            chart: {
                type: "gauge",
                height,
                width,
                events: {
                    load: ($event) => this.updateChartOptions($event)
                }
            },
            caption: {
                useHTML: true,
                text: `<div style="width:${width}px; font-family: 'OpenSans'; padding: 0px;" class="text-center">
                <span class="gauge-metric-name" id="metric-name-span">${gaugeData.MetricName}</span>
                <div class="text-center gauge-metric-type" id="metric-type-div">(${numberType})</div>
                </div>`
            },
            credits: {
                enabled: false
            },
            responsive: {
                rules: [
                    {
                        condition: {
                            maxWidth: 1400
                        },
                        chartOptions: {
                            title: {
                                text: centerTitle,
                                align: 'center',
                                verticalAlign: 'middle',
                                y: largeHeight,
                                style: {
                                    fontSize: '30px',
                                    fontWeight: '400',
                                    fontFamily: 'OpenSans',
                                    color: '#2E2E2E'
                                }
                            }
                        }
                    },
                    {
                        condition: {
                            maxWidth: 500
                        },
                        chartOptions: {
                            title: {
                                text: centerTitle,
                                align: 'center',
                                verticalAlign: 'middle',
                                y: mediumHeight,
                                style: {
                                    fontSize: '18px',
                                    fontWeight: '400',
                                    fontFamily: 'OpenSans',
                                    color: '#2E2E2E'
                                }
                            }
                        }
                    },
                    {
                        condition: {
                            maxWidth: 300
                        },
                        chartOptions: {
                            title: {
                                text: centerTitle,
                                align: 'center',
                                verticalAlign: 'middle',
                                y: smallHeight,
                                style: {
                                    fontSize: '12px',
                                    fontWeight: '400',
                                    fontFamily: 'OpenSans',
                                    color: '#2E2E2E'
                                }
                            }
                        }
                    }]
            },
            pane: {
                startAngle: -120,
                endAngle: 119,
                background: null,
                center: ["50%", "70%"],
                size: "100%",
            },
            yAxis: {
                min: 0,
                max: 200,
                tickPosition: "inside",
                tickColor: borderColor,
                tickLength: thickness,
                tickWidth: 1,
                tickPositions: tickPosition,
                minorTickInterval: null,
                lineWidth: 1,
                lineColor: borderColor,
                labels: {
                    distance: 22,
                    useHTML: true,
                    allowOverlap: false,
                    formatter: ($event) => {
                        const color = "#5B788E";
                        const valDiv = Number((Number($event.value) / div).toFixed(2));
                        let displayVal;
                        if (gaugeData.NumericType === 0) {
                            displayVal = this.numberConverter(valDiv);
                        } else if (gaugeData.NumericType === 2) {
                            const val = String(valDiv * 100);
                            displayVal = val.includes('.') ? (valDiv * 100).toFixed(2) + '%' : val + '%';
                        } else if (gaugeData.NumericType === 3) {
                            switch (gaugeData.TimeType) {
                                case 0:
                                case 1:
                                    displayVal = valDiv + ' Sec';
                                    break;
                                case 2:
                                    displayVal = valDiv / 60 + ' Min';
                                    break;
                                case 3:
                                    displayVal = ((valDiv / 60) / 60).toFixed(2) + ' H';
                                    break;
                                case 4:
                                    const d = valDiv;
                                    const h = Math.floor(d / 3600);
                                    const m = Math.floor(d % 3600 / 60);
                                    const s = Math.floor(d % 3600 % 60);
                                    displayVal = `${h < 10 ? '0' + h : h}:${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
                                    break;
                            }
                        } else {
                            displayVal = this.numberConverter(valDiv);
                        }
                        let x = '0';
                        let y = '0';
                        if (Number($event.value) < 100) {
                            x = '-7';
                            y = '2';
                        } else {
                            x = displayVal.length > 4 ? '10' : '2';
                            y = '6';
                        }
                        let fontSize = '11px';
                        if ($event.chart.chartWidth < 250) {
                            fontSize = '8px';
                            x = '0';
                            y = '0';
                        }
                        return `<span id="label-${this.moduleKey}-${$event.value}" 
                        style='color: ${color};display:inline-block;transform:translate(${x}px,${y}px); font-size:${fontSize}'>
                        ${displayVal}</span>`;
                    },
                },
                plotBands: plotBandsDefault,
            },
            tooltip: {
                enabled: false,
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        borderWidth: 0,
                        allowOverlap: true
                    },
                    enableMouseTracking: true,
                },
            },
            series: [
                {
                    data: breakPoint,
                    isTrianglePoint: true,
                    dial: {
                        backgroundColor: "#5B788E",
                        rearLength: "-90%",
                        size: 2,
                    },
                    pivot: {
                        radius: 0,
                    },
                    dataLabels: {
                        enabled: false
                    },
                },
            ] as any,
        };
        this.chartOptions = { ...{}, ...chartOptions };
        this.setDisplay();
    }

    updateChartOptions(chartOptions: Event) {
        this.chartData = chartOptions?.target;
        Highcharts.addEvent(this.chartData?.container, 'mouseenter', (e) => {
            this.chartOptions.yAxis['plotBands'] = [...[], ...this.plotbands];
            this.chartOptions = { ...{}, ...this.chartOptions };
            this.customPlugin(this.highcharts);
        });
        Highcharts.addEvent(this.chartData?.container, 'mouseleave', (e) => {
            this.chartOptions.yAxis['plotBands'] = [...[], ...this.plotBandsDefault];
            this.chartOptions = { ...{}, ...this.chartOptions };
            if (this.chartData.plotBandsTooltip && this.chartData.plotBandsTooltip.length > 0) {
                this.chartData.plotBandsTooltip.map(item => {
                    item.css({
                        visibility: 'hidden'
                    });
                });
            }
        });
    }

    numberConverter(labelValue) {
        let formattedValue = String(Math.abs(Number(labelValue)));
        if (Math.abs(Number(labelValue)) >= 1.0e+9) {
            formattedValue = Math.abs(Number(labelValue)) / 1.0e+9 + "B";
        } else if (Math.abs(Number(labelValue)) >= 1.0e+6) {
            formattedValue = Math.abs(Number(labelValue)) / 1.0e+6 + "M";
        } else if (Math.abs(Number(labelValue)) >= 1.0e+3) {
            formattedValue = Math.abs(Number(labelValue)) / 1.0e+3 + "K";
        }
        return formattedValue;
    }

    getDisplayValue(gaugeData: GaugeObject) {
        const gaugeValue: number = gaugeData.Value !== null ? gaugeData.Value : 0;
        let centerTitle: string = gaugeData.DisplayValue === 'N/A' ? '0' : gaugeData.DisplayValue;
        if (gaugeData.NumericType === 0) {
            centerTitle = gaugeValue.toFixed(2);
        } else if (gaugeData.NumericType === 2) {
            centerTitle = gaugeData.DisplayValue === 'N/A' ? '0%' : gaugeData.DisplayValue;
        } else if (gaugeData.NumericType === 3) {
            const timeTypes = ['', ' Sec', ' Min', ' H', ''];
            if (gaugeData.DisplayValue === 'N/A' && gaugeData.TimeType === 4) {
                centerTitle = '00:00:00';
            } else if (gaugeData.DisplayValue !== 'N/A') {
                centerTitle = gaugeData.DisplayValue + timeTypes[gaugeData.TimeType];
            } else {
                centerTitle = '0' + timeTypes[gaugeData.TimeType];
            }
        }
        this.gaugeDisplayValue = centerTitle;
        return centerTitle;
    }

    setDisplay() {
        if (this.moduleDisplay === GaugeModuleDisplay.Compact) {
            this.showColorIndicator = false;
        }
        else {
            this.showColorIndicator = true;
        }
    }

    resetMenuItems() {
        this.moduleManager.resetMenu();
        this.addMenuItems();
    }

    async addMenuItems() {
        const displayItems = [{
            name: "Compact",
            icon: '',
            click: () => {
                this.zone.run(() => {
                    this.changeAndSaveModuleDisplay(GaugeModuleDisplay.Compact);
                });
            }
        },
        {
            name: "Detailed",
            icon: '',
            click: () => {
                this.zone.run(() => {
                    this.changeAndSaveModuleDisplay(GaugeModuleDisplay.Detailed);
                });
            }
        }];

        this.moduleManager.addMenuItem({
            name: 'Widget Display',
            icon: 'chartType',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.gaugeData?.GaugeData?.Objective != null,
            subItems: displayItems,
            readOnlyEnabled: true
        } as ModuleManagerSubMenu);

    }

    changeAndSaveModuleDisplay(moduleDisplay: GaugeModuleDisplay) {
        if (this.moduleDisplay === moduleDisplay) // going to the same display view as currently shown
            return;
        this.moduleDisplay = moduleDisplay;
        if (this.moduleDisplay === GaugeModuleDisplay.Compact) {
            this.moduleManager.setSize(6, 6, true);//6 width 6 height
        }
        else {
            this.moduleManager.setSize(7, 7, true);//7 width 7 height
        }
        this.setDisplay();
        this.loadData(false);
        this.resetMenuItems();
        this.moduleManager.saveState('viewModelGauge');
        this.moduleManager.requestPublishNeeded();
    }

    getToolTip(ind) {
        const values = this.gaugeData.GaugeData.Objective.Values;
        const div = 200 / this.gaugeData.GaugeData.Objective.Max;
        if (ind === values.length) {
            return null;
        } else {
            const element = document.getElementById(`label-${this.moduleKey}-${values[ind] * div}`);
            return element ? element.innerText : null;
        }
    }

    customPlugin(highchart) {
        const width = document.getElementById("gauge-body-" + this.moduleKey).clientWidth;
        const H = highchart;
        const gaugeWidth = width;
        const { each, merge, pInt, pick, isNumber } = H;

        H.seriesTypes.gauge.prototype.translate = function () {
            const series = this;
            const yAxis = series.yAxis;
            const options = series.options;
            const center = yAxis.center;

            series.generatePoints();

            each(series.points, (point) => {
                const dialOptions = merge(options.dial, point.dial);
                const isTrianglePoint = point.series.userOptions.isTrianglePoint;
                const size = pInt(pick(dialOptions.size, 3));
                const radius = (pInt(pick(dialOptions.radius, 80)) * center[2]) / 200;
                const baseLength = (pInt(pick(dialOptions.baseLength, 70)) * radius) / 100;
                const rearLength = (pInt(pick(dialOptions.rearLength, 90)) * radius) / 100;
                const baseWidth = dialOptions.baseWidth || 3;
                const topWidth = dialOptions.topWidth || 1;
                let overshoot = options.overshoot;
                let rotation =
                    yAxis.startAngleRad +
                    yAxis.translate(point.y, null, null, null, true);

                // Handle the wrap and overshoot options
                if (isNumber(overshoot)) {
                    overshoot = (overshoot / 180) * Math.PI;
                    rotation = Math.max(
                        yAxis.startAngleRad - overshoot,
                        Math.min(yAxis.endAngleRad + overshoot, rotation)
                    );
                } else if (options.wrap === false) {
                    rotation = Math.max(
                        yAxis.startAngleRad,
                        Math.min(yAxis.endAngleRad, rotation)
                    );
                }

                rotation = (rotation * 180) / Math.PI;
                const responsiveValue = gaugeWidth < 300 ? 2 : 1;
                const triangleSize = responsiveValue === 1 ? (-rearLength + 8) : -rearLength;
                const translateX = rotation > 0 ? 3 : -3;
                // Checking series to draw dots
                if (isTrianglePoint) {
                    //draw new dial
                    point.shapeType = 'path';
                    point.shapeArgs = {
                        d: dialOptions.path || [
                            'M',
                            triangleSize,
                            0,
                            'L',
                            -rearLength - 8,
                            ((-baseWidth / 2) - 5) / responsiveValue,
                            -rearLength - 8,
                            -((-baseWidth / 2) - 5) / responsiveValue,
                            'z'
                        ],
                        translateX: center[0] + translateX,
                        translateY: center[1],
                        rotation: rotation > -150 ? rotation + 2 : rotation,
                        style: 'stroke: white; stroke-width: 1',
                    };
                }
                // Positions for data label
                point.plotX = center[0] + translateX;
                point.plotY = center[1];
            });
        }; // end of replaced function
    }
}
